import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2, Eye, Upload, Download, AlertTriangle, X, RefreshCw } from 'lucide-react'
import { useJobs } from '../hooks/useJobs'
import { fmtEUR, fmtNum, fmtBytes, fmtDateTime } from '../utils/format'
import { StatusBadge, Empty, Spinner, SectionHeader } from '../components/UI'
import { exportUrl, reprocessJob } from '../api'
import type { JobSummary } from '../types'

// ── Confirm Delete Modal ───────────────────────────────────────────────────────
function DeleteModal({
  job,
  onConfirm,
  onCancel,
  isDeleting,
}: {
  job: JobSummary
  onConfirm: () => void
  onCancel: () => void
  isDeleting: boolean
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(15,27,53,.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}
      onClick={onCancel}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460,
        boxShadow: '0 20px 60px rgba(0,0,0,.2)',
        overflow: 'hidden',
      }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid #fee2e2',
          background: '#fef2f2',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: '#fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlertTriangle size={18} color="#b91c1c" />
            </div>
            <div>
              <p style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>
                Delete permanently?
              </p>
              <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                This cannot be undone
              </p>
            </div>
          </div>
          <button onClick={onCancel} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#94a3b8', padding: 4,
          }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {/* File info */}
          <div style={{
            background: '#f8fafc', borderRadius: 10,
            padding: '12px 14px', marginBottom: 18,
            border: '1px solid #e2e8f0',
          }}>
            <p style={{ fontWeight: 600, fontSize: 13.5, color: '#0f172a', marginBottom: 6 }}>
              {job.original_filename}
            </p>
            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748b' }}>
              <span>{fmtDateTime(job.created_at)}</span>
              {job.total_vat_eur > 0 && (
                <span style={{ color: '#15803d', fontWeight: 600 }}>
                  {fmtEUR(job.total_vat_eur)} VAT
                </span>
              )}
            </div>
          </div>

          {/* What gets deleted */}
          <p style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 10 }}>
            The following will be deleted permanently:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
            {[
              { icon: '📄', label: 'Uploaded file',      desc: 'Removed from S3 / local disk' },
              { icon: '📊', label: 'VAT analysis report', desc: 'Excel export deleted from S3 / local disk' },
              { icon: '🗄️',  label: 'Job record',         desc: 'Removed from database' },
              { icon: '📋', label: 'Mongo log entry',    desc: 'Record deleted from vat_engine_logs' },
            ].map(item => (
              <div key={item.label} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', borderRadius: 8,
                background: '#fff', border: '1px solid #e2e8f0',
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{item.label}</p>
                  <p style={{ fontSize: 11.5, color: '#64748b' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onCancel}
              disabled={isDeleting}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 9,
                background: '#f1f5f9', color: '#475569',
                border: '1px solid #e2e8f0', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 9,
                background: isDeleting ? '#fca5a5' : '#dc2626',
                color: '#fff', border: 'none', cursor: isDeleting ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                transition: 'background .15s',
              }}
            >
              {isDeleting ? (
                <>
                  <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash2 size={14} />
                  Delete permanently
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Queue Page ─────────────────────────────────────────────────────────────────
export default function Queue() {
  const navigate  = useNavigate()
  const { jobs, isLoading, isError, upload, remove } = useJobs()
  const [confirmJob, setConfirmJob] = useState<JobSummary | null>(null)
  const [reprocessingId, setReprocessingId] = useState<string | null>(null)

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return
    await upload.mutateAsync({ files: Array.from(files), rateMode: 'reduced' })
  }

  const handleDeleteConfirm = () => {
    if (!confirmJob) return
    remove.mutate(confirmJob.id, {
      onSettled: () => setConfirmJob(null),
    })
  }

  const handleReprocess = async (job: JobSummary) => {
    const newMode = ((job as any).rate_mode === 'reduced') ? 'standard' : 'reduced'
    if (!window.confirm(`Re-process with ${newMode} rates?`)) return
    setReprocessingId(job.id)
    try {
      await reprocessJob(job.id, newMode)
    } finally {
      setReprocessingId(null)
    }
  }

  if (isLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
      <Spinner size={32} />
    </div>
  )

  return (
    <div>
      {/* Confirm modal */}
      {confirmJob && (
        <DeleteModal
          job={confirmJob}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmJob(null)}
          isDeleting={remove.isPending}
        />
      )}


      {/* Backend offline banner */}
      {isError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
          padding: '12px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <div>
            <p style={{ fontWeight: 600, color: '#b91c1c', fontSize: 13.5, margin: 0 }}>
              Cannot reach backend
            </p>
            <p style={{ color: '#b91c1c', fontSize: 12.5, margin: '2px 0 0', opacity: .8 }}>
              Make sure <code style={{ background: '#fee2e2', padding: '1px 5px', borderRadius: 4 }}>./start.sh</code> is running and the server is on port 8000.
            </p>
          </div>
        </div>
      )}

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Processing Queue</h1>
          <p style={{ fontSize: 13.5, color: '#475569' }}>
            {jobs.length} file{jobs.length !== 1 ? 's' : ''} · multiple files process simultaneously
          </p>
        </div>
        <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
          <Upload size={14} /> Add Files
          <input type="file" accept=".xlsx,.xls,.csv" multiple style={{ display: 'none' }}
            onChange={e => handleFiles(e.target.files)} />
        </label>
      </div>

      {jobs.length === 0 ? (
        <div className="card">
          <Empty
            icon="⏳" title="No files in queue"
            sub="Upload expense files to start processing"
            action={
              <label className="btn btn-primary" style={{ cursor: 'pointer', margin: '0 auto' }}>
                <Upload size={14} /> Upload Files
                <input type="file" accept=".xlsx,.xls,.csv" multiple style={{ display: 'none' }}
                  onChange={e => handleFiles(e.target.files)} />
              </label>
            }
          />
        </div>
      ) : (
        <>
          {/* Processing banners */}
          {jobs.filter(j => j.status === 'processing' || j.status === 'queued').map(j => (
            <div key={j.id} style={{
              background: '#eff6ff', border: '1px solid #bfdbfe',
              borderRadius: 12, padding: '16px 20px', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
              <Spinner size={24} />
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, color: '#1d4ed8', fontSize: 13.5 }}>{j.original_filename}</p>
                <p style={{ fontSize: 12, color: '#3b82f6', marginTop: 2 }}>
                  {j.status === 'queued' ? 'Queued — waiting…' : 'Analysing · Mapping countries · Calculating VAT…'}
                </p>
                <div style={{ marginTop: 8, height: 4, background: '#bfdbfe', borderRadius: 99, overflow: 'hidden' }}>
                  <div className="shimmer-bar" />
                </div>
              </div>
            </div>
          ))}

          {/* Jobs table */}
          <div className="card">
            <div className="card-header">
              <SectionHeader title="All Jobs" sub="Click a row to view results · Delete button permanently removes from S3, DB and Mongo" />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead><tr>
                  <th>File</th>
                  <th>Size</th>
                  <th style={{ textAlign: 'right' }}>Transactions</th>
                  <th style={{ textAlign: 'right' }}>VAT Potential</th>
                  <th>Rate Mode</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr></thead>
                <tbody>
                  {jobs.map(j => (
                    <tr key={j.id}
                      style={{ cursor: j.status === 'done' ? 'pointer' : 'default' }}
                      onClick={() => j.status === 'done' && navigate(`/queue/${j.id}`)}>
                      <td style={{ fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {j.original_filename}
                      </td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>{fmtBytes(j.file_size_bytes)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>
                        {j.total_transactions > 0 ? fmtNum(j.total_transactions) : '—'}
                      </td>
                      <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: '#15803d' }}>
                        {j.total_vat_eur > 0 ? fmtEUR(j.total_vat_eur) : '—'}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 5,
                          background: (j as any).rate_mode === 'standard' ? '#fffbeb' : '#f0fdf4',
                          color:      (j as any).rate_mode === 'standard' ? '#b45309'  : '#15803d',
                          border:     `1px solid ${(j as any).rate_mode === 'standard' ? '#fde68a' : '#bbf7d0'}`,
                        }}>
                          {(j as any).rate_mode === 'standard' ? 'Standard' : 'Reduced'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#475569' }}>{fmtDateTime(j.created_at)}</td>
                      <td onClick={e => e.stopPropagation()}><StatusBadge status={j.status} /></td>
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          {j.status === 'done' && (
                            <>
                              <button className="btn btn-outline btn-sm"
                                onClick={() => navigate(`/queue/${j.id}`)}>
                                <Eye size={11} />
                              </button>
                              <a href={exportUrl(j.id)} download className="btn btn-success btn-sm">
                                <Download size={11} />
                              </a>
                            </>
                          )}
                          {j.status === 'error' && (
                            <span style={{ fontSize: 11, color: '#b91c1c', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {j.error_message}
                            </span>
                          )}
                          {/* Delete — opens confirm modal */}
                          <button
                            className="btn btn-danger btn-sm"
                            title="Delete permanently from S3, database and Mongo"
                            onClick={() => setConfirmJob(j)}
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}